import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// import React, { FC, useEffect } from "react"; // Custom Code
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

/* Custom Code */
import { Dictionary } from "@insite/client-framework/Common/Types";
import getColor from "@insite/mobius/utilities/getColor";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import { IconMap } from "@store/Framework/Types/Icon";
import CarFill, { CarFillOffset } from "@store/Library/Components/Icons/Car/CarFill";
import ShoppingBagFill, { ShoppingBagFillOffset } from "@store/Library/Components/Icons/ShoppingBag/ShoppingBagFill";
import ShoppingCartFill, {
    ShoppingCartFillOffset,
} from "@store/Library/Components/Icons/ShoppingCart/ShoppingCartFill";
import HeaderLink, { HeaderLinkStyles } from "@store/Library/Components/Toolkit/HeaderLink";
import { headerHideForDesktopStyles } from "@store/Themes/common/mixins/header";
import React, { FC, useEffect, useMemo } from "react";
/* Custom Code */

/* Store Custom Code - Start */
import ShoppingCartLine, {
    ShoppingCartLineOffset,
} from "@store/Library/Components/Icons/ShoppingCart/ShoppingCartLine";
/* Store Custom Code - End */

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    // Custom Code
    headerLink?: HeaderLinkStyles;
}

export const cartLinkStyles: CartLinkStyles = {
    mobileCountText: {
        css: css`
            position: absolute;
            top: ${pxToRem(2)};
            right: ${pxToRem(2)};
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            text-align: center;
            font-size: ${pxToRem(10)};
            width: 1.6em;
            height: 1.6em;
            padding: ${pxToRem(1)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            border: 1px solid ${getColor("header")};
            background-color: ${getColor("header.accent")};
            color: ${getColor("header.accentContrast")};
            border-radius: 50%;
            letter-spacing: ${pxToRem(-0.5)};

            ${breakpoint("md")} {
                top: 0;
                right: 0;
                font-size: ${pxToRem(11)};
            }
        `,
    },
};

/* Custom Code */
const iconMap: Dictionary<IconMap | undefined> = {
    cart: {
        /* Store Custom Code - Start */
        src: ShoppingCartLine,
        offset: ShoppingCartLineOffset,
        /* Store Custom Code - End */
    },
    truck: {
        src: CarFill,
        offset: CarFillOffset,
    },
    bag: {
        src: ShoppingBagFill,
        offset: ShoppingBagFillOffset,
    },
    none: undefined,
};
/* Custom Code */

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay = 0; // Custom Code

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const baseStyles = useMergeStyles("cartLink", cartLinkStyles); // Custom Code

    // Removed - showIcon
    // Removed - showLabel

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[selectedIcon || "none"]; // Custom Code

    // Removed - routerLink

    /* Custom Code */
    const showIconOnly = visibilityState === "icon" || isCompactHeaderCart; // Custom Code
    const itemsText = retainedTotalCountToDisplay === 1 ? translate("Item") : translate("Items");

    const styles = useMemo(() => {
        if (showIconOnly) {
            return baseStyles;
        }

        return mergeProps<CartLinkStyles>(baseStyles, {
            mobileCountText: {
                css: headerHideForDesktopStyles(),
            },
        });
    }, [baseStyles, visibilityState]);
    /* Custom Code */

    // Removed - isCompactHeaderCart

    /* Custom Code */
    return (
        <HeaderLink
            href={cartUrl}
            iconSrc={icon?.src}
            iconOffset={icon?.offset}
            showIconOnly={showIconOnly}
            label={translate("Cart")}
            text={`${retainedTotalCountToDisplay} ${itemsText}`}
            extendedStyles={styles.headerLink}
            data-test-selector="cartLink"
        >
            {retainedTotalCountToDisplay ? (
                <Typography {...styles.mobileCountText} aria-hidden="true">
                    {retainedTotalCountToDisplay}
                </Typography>
            ) : null}
        </HeaderLink>
    );
    /* Custom Code */
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Icon and Label", // Custom Code
                        value: "both",
                    },
                    // Removed - Show Label Only
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    // Removed - None
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
                isRequired: true, // Custom Code
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
